import {
  useWSModalCustom,
  WSAlert,
  WSCard,
  WSControl,
  WSFlexBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSPill,
  WSPillProps
} from "@wingspanhq/fe-component-library";
import { PaymentRequirementStrategy } from "@wingspanhq/payments/dist/interfaces";
import { useWSMutation } from "@ws-react-query";
import { useEffect, useMemo, useState } from "react";
import { getPayeeName } from "../../modules/Payees/selectors/getPayeeNames";
import { LabelMethod } from "../../modules/Settings/components/LabelMethod";
import { useDefaultPaymentMethod } from "../../modules/Settings/components/payment/DefaultPaymentMethod";
import { QUERY_PAYEE, QUERY_PAYEES_LIST } from "../../query/payee/keys";
import { useQueryPayee } from "../../query/payee/queries/useQueryPayee";
import { QUERY_PAYEE_ROWS } from "../../query/search/payee/queries/usePayeeRowsQuery";
import { updatePayee } from "../../services/payees";
import { WSServiceError } from "../../utils/serviceHelper";

type Props = { payeeId: string } & WSModalCustomProps<any>;

export const ModalAutopaySettings: React.FC<Props> = ({
  payeeId,
  ...modalProps
}) => {
  const queryPayee = useQueryPayee(payeeId);

  const payeeName = useMemo(() => {
    if (!queryPayee.data) return;

    return getPayeeName(queryPayee.data, true);
  }, [queryPayee.data]);

  const paymentMethod = useDefaultPaymentMethod();

  const [autopayeEnabled, setAutopayEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (!queryPayee.data) {
      return;
    }

    setAutopayEnabled(
      queryPayee.data?.payerOwnedData.autoPayStrategy ===
        PaymentRequirementStrategy.All
    );
  }, [queryPayee.data, queryPayee.data?.payerOwnedData.autoPayStrategy]);

  const [save, saveMeta] = useWSMutation<
    void,
    WSServiceError,
    { enabled: boolean }
  >(
    async ({ enabled }) => {
      await updatePayee(payeeId, {
        payerOwnedData: {
          autoPayStrategy: enabled
            ? PaymentRequirementStrategy.All
            : PaymentRequirementStrategy.None
        }
      });
    },
    {
      dependencies: [QUERY_PAYEE, QUERY_PAYEES_LIST, QUERY_PAYEE_ROWS],
      onSuccess: () => {
        modalProps.onClose(undefined);
      }
    }
  );

  return (
    <WSModal
      title={`Autopay settings for ${payeeName}`}
      size="M"
      {...modalProps}
      divider
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: () => {
              modalProps.onClose(undefined);
            }
          },
          {
            loading: saveMeta.isLoading,
            label: "Save changes",
            onClick: () => {
              save({ enabled: autopayeEnabled });
            }
          }
        ]
      }}
    >
      <WSList gap="2XL">
        <WSCard>
          <WSFlexBox wrap="nowrap" justify="space-between" alignItems="center">
            {paymentMethod.id && paymentMethod.type ? (
              <LabelMethod id={paymentMethod.id} type={paymentMethod.type} />
            ) : null}

            <WSFlexBox wrap="nowrap" gap="S" alignItems="center">
              {autopayeEnabled ? (
                <WSPill {...pillAutopayOn} />
              ) : (
                <WSPill {...pillAutopayOff} />
              )}
              <WSControl
                type="switch"
                value={autopayeEnabled}
                onChange={setAutopayEnabled}
                label="Autopay"
              />
            </WSFlexBox>
          </WSFlexBox>
        </WSCard>
        <WSAlert theme="info" icon="info-circle" title="About autopay">
          Turning autopay on will charge your chosen payment method on the due
          date of each invoice this contractor sends.
        </WSAlert>
      </WSList>
    </WSModal>
  );
};

export const useModalAutopaySettings = () =>
  useWSModalCustom(ModalAutopaySettings);

export const pillAutopayOn: WSPillProps = {
  theme: "success",
  text: "On",
  badge: true
};

export const pillAutopayOff: WSPillProps = {
  text: "Off",
  badge: true
};
